import type { JSX } from 'react';
import { Fragment, useState } from 'react';
import { ActionIcon, Alert, Anchor, Badge, Button, Checkbox, Group, MultiSelect, NumberInput, Paper, Select, SimpleGrid, Stack, Switch, Table, Text, TextInput, Textarea, Tooltip } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { OFFER_LIMITS, calculateVariant, hasSubcontractors, isJointBid, type LineResult } from '@pst/shared';
import { offersApi, type Constellation, type OfferExtra, type OfferPosition, type OfferShare, type OfferVariantData } from '../../api';
import { formatAmount, formatEuro, formatHours, formatPercent } from '../../format';
import { AmountCell } from '../../ui/AmountCell';
import { EditableText } from '../../ui/EditableText';
import { Section } from '../../ui/Section';

type Change = (next: OfferVariantData) => void;

const emptyShare = (): OfferShare => ({ text: '', hours: null, fixed: null });
const move = <T,>(list: T[], i: number, by: number): T[] => {
  const j = i + by;
  if (j < 0 || j >= list.length) return list;
  const next = [...list];
  [next[i], next[j]] = [next[j]!, next[i]!];
  return next;
};

/**
 * The calculation of one variant, laid out like the template: roles and
 * rates, subcontractors, positions, extras, parameters, cut variants - and
 * the figures the template checks, recalculated while typing.
 */
export function OfferCalculation({ data, constellation, onChange }: { data: OfferVariantData; constellation: Constellation; onChange: Change }): JSX.Element {
  const r = calculateVariant(data);
  return (
    <Stack gap="md">
      <Figures data={data} result={r} />
      <PartiesSection data={data} constellation={constellation} onChange={onChange} />
      <RolesSection data={data} onChange={onChange} />
      {(hasSubcontractors(constellation) || data.subcontractors.length > 0) && <SubcontractorsSection data={data} onChange={onChange} />}
      <LinesSection kind="positions" data={data} results={r.positions} onChange={onChange} />
      <LinesSection kind="extras" data={data} results={r.extras} onChange={onChange} />
      <ParamsSection data={data} onChange={onChange} />
      <CutsSection data={data} result={r} onChange={onChange} />
    </Stack>
  );
}

// --- Figures ----------------------------------------------------------------

function Figures({ data, result: r }: { data: OfferVariantData; result: ReturnType<typeof calculateVariant> }): JSX.Element {
  const unconfirmed = data.subcontractors.some((s) => s.priceStatus === 'Annahme');
  const tile = (label: string, value: string, note?: string, color?: string): JSX.Element => (
    <Paper withBorder radius="md" p="sm">
      <Text size="xs" c="dimmed">{label}</Text>
      <Text fw={600} size="lg" className="pst-tabular" c={color}>{value}</Text>
      {note && <Text size="xs" c="dimmed">{note}</Text>}
    </Paper>
  );
  return (
    <Stack gap="xs">
      <SimpleGrid cols={{ base: 2, sm: 3, lg: 6 }} spacing="sm">
        {tile('Angebotssumme netto', formatEuro(r.net), `brutto ${formatEuro(r.gross)}`)}
        {tile('Eigene Stunden', formatHours(r.ownHours), r.subcontractorHours ? `dazu ${formatHours(r.subcontractorHours)} Nachunternehmer` : undefined)}
        {tile('Erlös je eigener Stunde', r.revenuePerHour === null ? '—' : `${formatAmount(r.revenuePerHour)} €`, `Warnschwelle ${formatAmount(r.threshold)} €`, r.belowThreshold ? 'red.7' : undefined)}
        {tile('Aufrundungsreserve', formatEuro(r.rounding), 'die ganze Reserve für Fahrten, Kopien, Kommunikation')}
        {tile('Fremdleistung', formatEuro(r.foreign), r.net ? `${formatPercent(r.foreignShare * 100)} der Summe` : undefined)}
        {tile('Nach Nachlass', formatEuro(r.afterDiscount), r.discountHours ? `${formatPercent(data.params.discount * 100)} Nachlass = ${formatHours(r.discountHours)} unbezahlt` : undefined)}
      </SimpleGrid>
      {r.belowThreshold && <Alert color="red" py={6}>Der mittlere Erlös je eigener Stunde liegt unter der Warnschwelle – die Kalkulation ist nicht auskömmlich.</Alert>}
      {unconfirmed && r.foreign > 0 && <Alert color="orange" py={6}>Preise von Nachunternehmern sind Annahmen, solange kein schriftliches Festpreisangebot vorliegt – die Angebotssumme ist bis dahin nicht belastbar.</Alert>}
    </Stack>
  );
}

// --- Parties ------------------------------------------------------------------

function PartiesSection({ data, constellation, onChange }: { data: OfferVariantData; constellation: Constellation; onChange: Change }): JSX.Element {
  const parties = useQuery({ queryKey: ['offer-parties'], queryFn: offersApi.parties });
  const people = (parties.data ?? []).map((p) => ({ value: p.id, label: `${p.name}${p.profession ? `, ${p.profession}` : ''}` }));
  return (
    <Section title="Beteiligte" description={isJointBid(constellation) ? 'Mitglieder der Bietergemeinschaft, das erste führt. Stammdaten unter Angebote › Beteiligte.' : 'Der Bieter. Nachunternehmer stehen weiter unten. Stammdaten unter Angebote › Beteiligte.'}>
      <MultiSelect size="xs" data={people} value={data.partyIds} onChange={(ids) => onChange({ ...data, partyIds: ids })} maxValues={isJointBid(constellation) ? OFFER_LIMITS.parties : 1}
        placeholder={people.length ? 'wählen …' : 'Noch keine Beteiligten angelegt'} searchable comboboxProps={{ withinPortal: true }} />
      {isJointBid(constellation) && data.partyIds.length < 2 && <Text size="xs" c="orange.7" mt={4}>Eine Bietergemeinschaft braucht mindestens zwei Mitglieder.</Text>}
    </Section>
  );
}

// --- Roles ----------------------------------------------------------------------

function RolesSection({ data, onChange }: { data: OfferVariantData; onChange: Change }): JSX.Element {
  const setRole = (i: number, patch: Partial<OfferVariantData['roles'][number]>): void => onChange({ ...data, roles: data.roles.map((x, k) => (k === i ? { ...x, ...patch } : x)) });
  const addRole = (): void => {
    const short = `R${data.roles.length + 1}`;
    onChange({
      ...data,
      roles: [...data.roles, { short, name: '', rate: null, basis: '' }],
      positions: data.positions.map((p) => ({ ...p, hours: [...p.hours, null] })),
      extras: data.extras.map((e) => ({ ...e, hours: [...e.hours, null] })),
    });
  };
  const removeRole = (i: number): void => onChange({
    ...data,
    roles: data.roles.filter((_, k) => k !== i),
    positions: data.positions.map((p) => ({ ...p, hours: p.hours.filter((_, k) => k !== i) })),
    extras: data.extras.map((e) => ({ ...e, hours: e.hours.filter((_, k) => k !== i) })),
  });
  return (
    <Section title="Rollen und Stundensätze" description="Eigenleistung. Rolle 1 ist der Bieter selbst. Der Satz ergibt sich aus Jahreskosten plus angestrebter Entnahme, geteilt durch realistisch abrechenbare Stunden; Sätze aus anderen Projekten sind nicht übertragbar."
      actions={data.roles.length < OFFER_LIMITS.roles ? <Button size="xs" variant="light" onClick={addRole}>+ Rolle</Button> : undefined}>
      <Table verticalSpacing={3}>
        <Table.Thead><Table.Tr><Table.Th w={30} /><Table.Th w={80}>Kürzel</Table.Th><Table.Th>Rolle</Table.Th><Table.Th w={110} ta="right">Satz €/h</Table.Th><Table.Th>Herkunft und Bemessung [A]</Table.Th></Table.Tr></Table.Thead>
        <Table.Tbody>
          {data.roles.map((role, i) => (
            <Table.Tr key={i}>
              <Table.Td>{data.roles.length > 1 && <ActionIcon size="xs" variant="subtle" color="gray" aria-label="Rolle entfernen" onClick={() => removeRole(i)}>✕</ActionIcon>}</Table.Td>
              <Table.Td><EditableText value={role.short} monospace onCommit={(v) => setRole(i, { short: v.slice(0, 4) })} /></Table.Td>
              <Table.Td><EditableText value={role.name} placeholder="z. B. Architekt" allowEmpty onCommit={(v) => setRole(i, { name: v })} /></Table.Td>
              <Table.Td><AmountCell value={role.rate} onCommit={(v) => setRole(i, { rate: v })} /></Table.Td>
              <Table.Td><EditableText value={role.basis} size="xs" allowEmpty placeholder="Herleitung des Satzes" onCommit={(v) => setRole(i, { basis: v })} /></Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Section>
  );
}

// --- Subcontractors -----------------------------------------------------------------

function SubcontractorsSection({ data, onChange }: { data: OfferVariantData; onChange: Change }): JSX.Element {
  const parties = useQuery({ queryKey: ['offer-parties'], queryFn: offersApi.parties });
  const set = (i: number, patch: Partial<OfferVariantData['subcontractors'][number]>): void =>
    onChange({ ...data, subcontractors: data.subcontractors.map((x, k) => (k === i ? { ...x, ...patch } : x)) });
  const add = (): void => onChange({
    ...data,
    subcontractors: [...data.subcontractors, { partyId: null, name: '', short: `N${data.subcontractors.length + 1}`, area: '', billing: 'Stunden', purchaseRate: null, surcharge: 0.05, priceStatus: 'Annahme', address: '', contact: '' }],
    positions: data.positions.map((p) => ({ ...p, shares: [...p.shares, emptyShare()] })),
    extras: data.extras.map((e) => ({ ...e, shares: [...e.shares, emptyShare()] })),
  });
  const remove = (i: number): void => onChange({
    ...data,
    subcontractors: data.subcontractors.filter((_, k) => k !== i),
    positions: data.positions.map((p) => ({ ...p, shares: p.shares.filter((_, k) => k !== i) })),
    extras: data.extras.map((e) => ({ ...e, shares: e.shares.filter((_, k) => k !== i) })),
  });
  const choose = (i: number, id: string | null): void => {
    const p = parties.data?.find((x) => x.id === id);
    set(i, p ? { partyId: p.id, name: p.name, address: [p.street, p.city].filter(Boolean).join(', '), contact: [p.contact, p.phone, p.email].filter(Boolean).join(', ') } : { partyId: null });
  };
  return (
    <Section title="Nachunternehmer" description="Bis zu drei. Ohne schriftliche Preisbestätigung bleibt der Preis eine Annahme; der Zuschlag deckt Koordination und bei unbestätigtem Preis zusätzlich Sicherheit."
      actions={data.subcontractors.length < OFFER_LIMITS.subcontractors ? <Button size="xs" variant="light" onClick={add}>+ Nachunternehmer</Button> : undefined}>
      {data.subcontractors.length === 0 ? <Text size="sm" c="dimmed">Keine.</Text> : (
        <Table.ScrollContainer minWidth={900}>
          <Table verticalSpacing={3}>
            <Table.Thead><Table.Tr><Table.Th w={30} /><Table.Th w={220}>aus den Beteiligten</Table.Th><Table.Th>Firma / Name</Table.Th><Table.Th w={60}>Kürzel</Table.Th><Table.Th>Leistungsbereich</Table.Th><Table.Th w={110}>Abrechnung</Table.Th><Table.Th w={100} ta="right">Einkauf €/h</Table.Th><Table.Th w={90} ta="right">Zuschlag %</Table.Th><Table.Th w={110}>Preis</Table.Th></Table.Tr></Table.Thead>
            <Table.Tbody>
              {data.subcontractors.map((s, i) => (
                <Table.Tr key={i}>
                  <Table.Td><ActionIcon size="xs" variant="subtle" color="gray" aria-label="Nachunternehmer entfernen" onClick={() => remove(i)}>✕</ActionIcon></Table.Td>
                  <Table.Td><Select size="xs" data={(parties.data ?? []).map((p) => ({ value: p.id, label: p.name }))} value={s.partyId} onChange={(v) => choose(i, v)} clearable searchable comboboxProps={{ withinPortal: true }} placeholder="wählen …" /></Table.Td>
                  <Table.Td><EditableText value={s.name} allowEmpty placeholder="Firma" onCommit={(v) => set(i, { name: v })} /></Table.Td>
                  <Table.Td><EditableText value={s.short} monospace onCommit={(v) => set(i, { short: v.slice(0, 3) })} /></Table.Td>
                  <Table.Td><EditableText value={s.area} allowEmpty size="xs" placeholder="Kurztext fürs Preisblatt" onCommit={(v) => set(i, { area: v })} /></Table.Td>
                  <Table.Td><Select size="xs" data={['Stunden', 'Festpreis']} value={s.billing} onChange={(v) => v && set(i, { billing: v as 'Stunden' | 'Festpreis' })} allowDeselect={false} comboboxProps={{ withinPortal: true }} /></Table.Td>
                  <Table.Td><AmountCell value={s.purchaseRate} readOnly={s.billing !== 'Stunden'} onCommit={(v) => set(i, { purchaseRate: v })} /></Table.Td>
                  <Table.Td><AmountCell value={Math.round(s.surcharge * 10000) / 100} quantity onCommit={(v) => set(i, { surcharge: (v ?? 0) / 100 })} /></Table.Td>
                  <Table.Td><Select size="xs" data={['Annahme', 'bestätigt']} value={s.priceStatus} onChange={(v) => v && set(i, { priceStatus: v as 'Annahme' | 'bestätigt' })} allowDeselect={false} comboboxProps={{ withinPortal: true }} /></Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
    </Section>
  );
}

// --- Positions and extras --------------------------------------------------------

function LinesSection({ kind, data, results, onChange }: { kind: 'positions' | 'extras'; data: OfferVariantData; results: LineResult[]; onChange: Change }): JSX.Element {
  const [open, setOpen] = useState<number | null>(null);
  const isPos = kind === 'positions';
  const lines = (isPos ? data.positions : data.extras) as (OfferPosition | OfferExtra)[];
  const limit = isPos ? OFFER_LIMITS.positions : OFFER_LIMITS.extras;
  const put = (next: (OfferPosition | OfferExtra)[]): void => onChange({ ...data, [kind]: next });
  const set = (i: number, patch: Partial<OfferPosition & OfferExtra>): void => put(lines.map((l, k) => (k === i ? { ...l, ...patch } : l)));
  const add = (): void => {
    const hours = data.roles.map(() => null);
    const shares = data.subcontractors.map(emptyShare);
    const line = isPos
      ? { title: '', hours, shares, kind: 'Pauschale', reasoning: '', owed: '', excluded: '', cuts: [true, true, true] } satisfies OfferPosition
      : { title: '', hours, shares, unit: '', reasoning: '', origin: 'Vorschlag', description: '' } satisfies OfferExtra;
    put([...lines, line]);
    setOpen(lines.length);
  };
  const setShare = (i: number, s: number, patch: Partial<OfferShare>): void => set(i, { shares: lines[i]!.shares.map((x, k) => (k === s ? { ...x, ...patch } : x)) });
  const label = (i: number): string => (isPos ? String(i + 1) : `E${i + 1}`);
  const cols = 4 + data.roles.length + data.subcontractors.length * 2 + 4;
  return (
    <Section
      title={isPos ? 'Positionen' : 'Zusatzleistungen'}
      description={isPos
        ? 'In der Reihenfolge der Auslobung. Jeder Stundenansatz ist eine Annahme [A] und wird begründet: was wird geliefert, wie viele Stunden wofür. Eine Position ohne Stunden und Fremdleistung zählt nicht.'
        : 'Alles, was in den Pauschalen nicht steckt: weitere Runden, Termine, Szenarien, Optionen – je Einheit. Ohne Stunden und Einkauf erscheint eine Leistung als „auf Anfrage“.'}
      actions={lines.length < limit ? <Button size="xs" variant="light" onClick={add}>+ {isPos ? 'Position' : 'Zusatzleistung'}</Button> : undefined}
    >
      {lines.length === 0 ? <Text size="sm" c="dimmed">Noch keine.</Text> : (
        <Table.ScrollContainer minWidth={900}>
          <Table verticalSpacing={3} className="pst-tabular">
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={54} />
                <Table.Th w={40}>{isPos ? 'Pos.' : 'Nr.'}</Table.Th>
                <Table.Th>Leistung</Table.Th>
                {!isPos && <Table.Th w={90}>Einheit</Table.Th>}
                {data.roles.map((r, k) => <Table.Th key={k} w={70} ta="right" title={r.name}>{r.short} h</Table.Th>)}
                {data.subcontractors.map((s, k) => (
                  <Fragment key={k}><Table.Th w={64} ta="right" title={s.name}>{s.short} h</Table.Th><Table.Th w={90} ta="right" title={`${s.name}: Festpreis`}>{s.short} fest €</Table.Th></Fragment>
                ))}
                <Table.Th w={100} ta="right">Eigen €</Table.Th>
                <Table.Th w={100} ta="right">Fremd €</Table.Th>
                <Table.Th w={110} ta="right">{isPos ? 'Preis €' : 'Einheitspreis €'}</Table.Th>
                {isPos && <Table.Th w={80} ta="right">Aufr. €</Table.Th>}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {lines.map((l, i) => {
                const res = results[i]!;
                const missing = !l.reasoning.trim() && (res.ownHours > 0);
                return (
                  <Fragment key={i}>
                    <Table.Tr style={open === i ? { background: 'var(--mantine-color-default-hover)' } : undefined}>
                      <Table.Td>
                        <Group gap={0} wrap="nowrap">
                          <ActionIcon size="xs" variant="subtle" color="gray" aria-label="entfernen" title="entfernen" onClick={() => put(lines.filter((_, k) => k !== i))}>✕</ActionIcon>
                          <ActionIcon size="xs" variant="subtle" color="gray" aria-label="nach oben" title="nach oben" onClick={() => put(move(lines, i, -1))}>↑</ActionIcon>
                        </Group>
                      </Table.Td>
                      <Table.Td><Text size="sm" ff="monospace">{label(i)}</Text></Table.Td>
                      <Table.Td>
                        <Group gap={4} wrap="nowrap">
                          <div style={{ flex: 1, minWidth: 0 }}><EditableText value={l.title} allowEmpty placeholder="Leistungsbezeichnung" onCommit={(v) => set(i, { title: v })} /></div>
                          <Tooltip label={missing ? 'Begründung des Stundenansatzes fehlt' : 'Begründung und Texte'}>
                            <Anchor size="xs" component="button" type="button" c={missing ? 'orange.7' : 'dimmed'} onClick={() => setOpen(open === i ? null : i)}>{open === i ? '▾' : '▸'} Texte</Anchor>
                          </Tooltip>
                        </Group>
                      </Table.Td>
                      {!isPos && <Table.Td><EditableText value={(l as OfferExtra).unit} size="xs" allowEmpty placeholder="je …" onCommit={(v) => set(i, { unit: v })} /></Table.Td>}
                      {data.roles.map((_, k) => (
                        <Table.Td key={k}><AmountCell value={l.hours[k] ?? null} quantity onCommit={(v) => set(i, { hours: l.hours.map((h, j) => (j === k ? v : h)) })} /></Table.Td>
                      ))}
                      {data.subcontractors.map((s, k) => (
                        <Fragment key={k}>
                          <Table.Td><AmountCell value={l.shares[k]?.hours ?? null} quantity readOnly={s.billing !== 'Stunden'} onCommit={(v) => setShare(i, k, { hours: v })} /></Table.Td>
                          <Table.Td><AmountCell value={l.shares[k]?.fixed ?? null} onCommit={(v) => setShare(i, k, { fixed: v })} /></Table.Td>
                        </Fragment>
                      ))}
                      <Table.Td><AmountCell value={res.own || null} readOnly dimmed /></Table.Td>
                      <Table.Td><AmountCell value={res.foreign || null} readOnly dimmed /></Table.Td>
                      <Table.Td>{res.price === null ? <Text size="xs" c="dimmed" ta="right">auf Anfrage</Text> : <AmountCell value={res.price} readOnly bold />}</Table.Td>
                      {isPos && <Table.Td><AmountCell value={res.rounding || null} readOnly dimmed /></Table.Td>}
                    </Table.Tr>
                    {open === i && (
                      <Table.Tr>
                        <Table.Td colSpan={cols}>
                          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xs">
                            <Textarea size="xs" label="Begründung des Stundenansatzes [A] – intern" description="was wird geliefert, wie viele Stunden wofür (Spalte P und Zellkommentar)" autosize minRows={2} value={l.reasoning} onChange={(e) => set(i, { reasoning: e.currentTarget.value })} />
                            {isPos ? (
                              <>
                                <Textarea size="xs" label="Geschuldet – im Angebot" description="was geliefert wird, in Artefakten: Bericht, Plan mit Maßstab, Anzahl Schnitte, Szenarien, Termine" autosize minRows={2} value={(l as OfferPosition).owed} onChange={(e) => set(i, { owed: e.currentTarget.value })} />
                                <Textarea size="xs" label="Nicht enthalten – im Angebot" description="was ausdrücklich nicht geschuldet ist, mit der Zusatzleistung, unter der es beauftragt werden kann" autosize minRows={2} value={(l as OfferPosition).excluded} onChange={(e) => set(i, { excluded: e.currentTarget.value })} />
                                <TextInput size="xs" label="Art" value={(l as OfferPosition).kind} onChange={(e) => set(i, { kind: e.currentTarget.value })} />
                              </>
                            ) : (
                              <>
                                <Textarea size="xs" label="Kurzbeschreibung – im Angebot" description="Umfang je Einheit, Auslöser, Abgrenzung" autosize minRows={2} value={(l as OfferExtra).description} onChange={(e) => set(i, { description: e.currentTarget.value })} />
                                <Select size="xs" label="Herkunft" data={[{ value: 'verlangt', label: 'von der Auslobung verlangt' }, { value: 'Vorschlag', label: 'eigener Vorschlag' }]} value={(l as OfferExtra).origin} onChange={(v) => v && set(i, { origin: v as 'verlangt' | 'Vorschlag' })} allowDeselect={false} comboboxProps={{ withinPortal: true }} />
                              </>
                            )}
                            {data.subcontractors.map((s, k) => (
                              <TextInput key={k} size="xs" label={`Anteil ${s.short || `N${k + 1}`}${s.name ? ` (${s.name})` : ''}`} description="was der Nachunternehmer darin leistet" value={l.shares[k]?.text ?? ''} onChange={(e) => setShare(i, k, { text: e.currentTarget.value })} />
                            ))}
                          </SimpleGrid>
                          {res.hoursNote && <Text size="xs" c="dimmed" mt={4}>Ausweis im Preisblatt: {res.hoursNote}{typeof res.rateNote === 'number' ? ` × ${formatAmount(res.rateNote)} €/h` : res.rateNote ? ` · ${res.rateNote}` : ''}</Text>}
                        </Table.Td>
                      </Table.Tr>
                    )}
                  </Fragment>
                );
              })}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
    </Section>
  );
}

// --- Parameters ---------------------------------------------------------------

function ParamsSection({ data, onChange }: { data: OfferVariantData; onChange: Change }): JSX.Element {
  const p = data.params;
  const set = (patch: Partial<typeof p>): void => onChange({ ...data, params: { ...p, ...patch } });
  const pct = (label: string, value: number, key: 'surchargeOwn' | 'vat' | 'discount', description?: string): JSX.Element => (
    <NumberInput size="xs" label={label} description={description} value={Math.round(value * 10000) / 100} suffix=" %" decimalSeparator="," min={0} max={100} onChange={(v) => set({ [key]: (typeof v === 'number' ? v : 0) / 100 })} />
  );
  return (
    <Section title="Parameter" description="Die Voreinstellungen sind Ausgangswerte, keine Empfehlung.">
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="xs">
        {pct('Zuschlag auf Eigenleistung', p.surchargeOwn, 'surchargeOwn', 'Vorgabe 0 %: Dann ist die Aufrundung die einzige Reserve.')}
        <NumberInput size="xs" label="Aufrundung Positionen €" value={p.roundPositions} min={1} onChange={(v) => set({ roundPositions: typeof v === 'number' && v > 0 ? v : 100 })} description="immer aufwärts" />
        <NumberInput size="xs" label="Aufrundung Einheitspreise €" value={p.roundExtras} min={1} onChange={(v) => set({ roundExtras: typeof v === 'number' && v > 0 ? v : 50 })} />
        {pct('Umsatzsteuer', p.vat, 'vat', 'bei Kleinunternehmerregelung 0 % – steuerlich prüfen lassen')}
        <NumberInput size="xs" label="Warnschwelle €/h (leer = niedrigster Satz)" value={p.minRate ?? ''} min={0} decimalSeparator="," onChange={(v) => set({ minRate: typeof v === 'number' ? v : null })} />
        {pct('Nachlass für die Verhandlung', p.discount, 'discount', 'nur zur Rechnung, geht in kein Dokument')}
        <Switch size="xs" mt="lg" label="Stunden und Satz im Preisblatt ausweisen" checked={p.showHours} onChange={(e) => set({ showHours: e.currentTarget.checked })} description="nur wenn die Auslobung es verlangt" />
        <Switch size="xs" mt="lg" label="Leistungsanteil der Nachunternehmer als Anlage" checked={p.subcontractorAnnex} onChange={(e) => set({ subcontractorAnnex: e.currentTarget.checked })} />
      </SimpleGrid>
      <SimpleGrid cols={{ base: 1, md: 3 }} spacing="xs" mt="xs">
        <Textarea size="xs" label="Hinweis unter der Preisübersicht" autosize minRows={2} value={p.hintPrices} onChange={(e) => set({ hintPrices: e.currentTarget.value })} />
        <Textarea size="xs" label="Hinweis unter den Zusatzleistungen" autosize minRows={2} value={p.hintExtras} onChange={(e) => set({ hintExtras: e.currentTarget.value })} />
        <Textarea size="xs" label="Hinweis in der Anlage Nachunternehmer" autosize minRows={2} value={p.subcontractorHint} onChange={(e) => set({ subcontractorHint: e.currentTarget.value })} />
      </SimpleGrid>
    </Section>
  );
}

// --- Cut variants ------------------------------------------------------------------

function CutsSection({ data, result: r, onChange }: { data: OfferVariantData; result: ReturnType<typeof calculateVariant>; onChange: Change }): JSX.Element {
  const setCut = (i: number, c: number, on: boolean): void => onChange({ ...data, positions: data.positions.map((p, k) => (k === i ? { ...p, cuts: p.cuts.map((x, j) => (j === c ? on : x)) } : p)) });
  const names = data.cutNames.map((n, i) => n || `Variante ${String.fromCharCode(65 + i)}`);
  return (
    <Section title="Zuschnitt bei zu kleinem Budget" description="Nicht der Preis wird gesenkt, sondern der Umfang: bis zu drei Zuschnitte aus einzeln beauftragbaren Positionen. Intern.">
      {data.positions.length === 0 ? <Text size="sm" c="dimmed">Erst Positionen anlegen.</Text> : (
        <Table.ScrollContainer minWidth={600}>
          <Table verticalSpacing={2} className="pst-tabular">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Position</Table.Th>
                {names.map((n, c) => (
                  <Table.Th key={c} w={170}><EditableText value={data.cutNames[c] ?? ''} size="xs" allowEmpty placeholder={n} onCommit={(v) => onChange({ ...data, cutNames: data.cutNames.map((x, j) => (j === c ? v : x)) })} /></Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {data.positions.map((p, i) => (
                <Table.Tr key={i}>
                  <Table.Td><Text size="sm">{i + 1} {p.title || '—'}</Text></Table.Td>
                  {names.map((_, c) => <Table.Td key={c}><Checkbox size="xs" checked={p.cuts[c] ?? false} onChange={(e) => setCut(i, c, e.currentTarget.checked)} aria-label={`${names[c]}: Position ${i + 1}`} /></Table.Td>)}
                </Table.Tr>
              ))}
              <Table.Tr style={{ borderTop: '2px solid var(--mantine-color-default-border)' }}>
                <Table.Td><Text size="sm" fw={600}>Summe netto · Stunden · Erlös je Stunde</Text></Table.Td>
                {r.cuts.map((c, k) => (
                  <Table.Td key={k}>
                    <Text size="sm" fw={600}>{formatEuro(c.net)}</Text>
                    <Text size="xs" c={c.revenuePerHour !== null && c.revenuePerHour < r.threshold ? 'red.7' : 'dimmed'}>{formatHours(c.ownHours)} · {c.revenuePerHour === null ? '—' : `${formatAmount(c.revenuePerHour)} €/h`}</Text>
                  </Table.Td>
                ))}
              </Table.Tr>
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
      <Badge size="xs" variant="light" color="gray" mt="xs">intern – nicht zur Weitergabe</Badge>
    </Section>
  );
}
